import React, { useEffect, useRef, useMemo, memo, useCallback } from "react";
import { Loader } from "lucide-react";

// Memoize individual suggestion rows
const SuggestionRow = memo(({ suggestion, isSelected, onSelect, onHover }) => (
  <tr
    className={`cursor-pointer transition-colors ${
      isSelected ? "bg-blue-200" : "hover:bg-blue-100"
    }`}
    onMouseDown={(e) => {
      e.preventDefault();
      e.stopPropagation();
      onSelect(suggestion);
    }}
    onMouseEnter={onHover}
  >
    <td className="px-3 py-2 text-sm text-gray-800 max-w-sm">
      {suggestion.Code}
    </td>
    <td
      title={suggestion.Description}
      className="px-3 py-2 text-sm text-gray-600 truncate max-w-sm"
    >
      {suggestion.Description}
    </td>
    <td className="px-3 py-2 text-sm text-gray-600">{suggestion.Unit}</td>
    <td className="px-3 py-2 text-sm text-gray-600">{suggestion.Category}</td>
    <td className="px-3 py-2 text-sm text-gray-600">{suggestion.Ttl_Stk}</td>
    <td className="px-3 py-2 text-sm text-gray-600">{suggestion.Br_Stk}</td>
    <td className="px-3 py-2 text-sm text-gray-600">{suggestion.Group}</td>
    <td className="px-3 py-2 text-sm text-gray-600">{suggestion.sgroup}</td>
    <td className="px-3 py-2 text-sm text-gray-600">{suggestion.SelPrice}</td>
  </tr>
));

const InvoiceSuggestionList = ({
  suggestions,
  selectCodeSuggestion,
  selectedSuggestionIndex,
  setSelectedSuggestionIndex,
  activeSuggestionRowId,
  showSuggestions,
  loading,
  hasMoreSuggestions,
  isFetchingMore,
  onLoadMore,
  currentQuery,
}) => {
  const rowRefs = useRef([]);
  const suggestionListRef = useRef(null);
  const loadMoreTriggerRef = useRef(null);
  const prevScrollHeightRef = useRef(0);

  const optimizedStyles = {
    table: {
      contain: "layout style paint",
      willChange: "transform",
    },
    row: {
      contain: "layout paint",
    },
  };

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const triggerElement = loadMoreTriggerRef.current;
    const containerElement = suggestionListRef.current;

    if (!triggerElement || !containerElement) return;
    if (!hasMoreSuggestions || isFetchingMore) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isFetchingMore && hasMoreSuggestions) {
          prevScrollHeightRef.current = containerElement.scrollHeight;
          onLoadMore(currentQuery);
        }
      },
      {
        root: containerElement,
        rootMargin: "50px",
        threshold: 0.1,
      },
    );

    observer.observe(triggerElement);

    return () => observer.disconnect();
  }, [
    hasMoreSuggestions, // stop when false
    isFetchingMore, // reattach after fetch completes
    onLoadMore,
    currentQuery,
  ]);
  useEffect(() => {
    if (!isFetchingMore && prevScrollHeightRef.current) {
      const container = suggestionListRef.current;
      container.scrollTop =
        container.scrollHeight - prevScrollHeightRef.current;
    }
  }, [suggestions]);

  useEffect(() => {
    if (isFetchingMore) return;

    if (rowRefs.current[selectedSuggestionIndex]) {
      rowRefs.current[selectedSuggestionIndex].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [selectedSuggestionIndex, isFetchingMore]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showSuggestions || suggestions.length === 0) return;

      const activeElement = document.activeElement;
      const isCodeInput =
        activeElement &&
        activeElement.closest('[data-field="code"]') &&
        activeElement.closest(`[data-row-id="${activeSuggestionRowId}"]`);

      if (!isCodeInput) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        setSelectedSuggestionIndex((prevIndex) =>
          prevIndex < suggestions.length - 1 ? prevIndex + 1 : 0,
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        setSelectedSuggestionIndex((prevIndex) =>
          prevIndex > 0 ? prevIndex - 1 : suggestions.length - 1,
        );
      } else if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        if (
          selectedSuggestionIndex >= 0 &&
          selectedSuggestionIndex < suggestions.length
        ) {
          selectCodeSuggestion(
            activeSuggestionRowId,
            suggestions[selectedSuggestionIndex],
          );
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        setSelectedSuggestionIndex(-1);
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [
    suggestions,
    selectedSuggestionIndex,
    setSelectedSuggestionIndex,
    selectCodeSuggestion,
    activeSuggestionRowId,
    showSuggestions,
  ]);

  if (!showSuggestions) return null;

  return (
    <div
      ref={suggestionListRef}
      className="absolute left-10 right-0 bg-white border border-gray-300 max-h-60 overflow-y-auto text-sm shadow-lg rounded-md rounded-t-none z-20 max-w-[calc(100vw-15vw)]"
    >
      {loading ? (
        <div className="flex flex-col items-center justify-center py-3">
          <Loader className="animate-spin h-8 w-8 text-gray-500 mb-3" />
          <p className="text-sm text-gray-500">Loading Products...</p>
        </div>
      ) : suggestions.length > 0 ? (
        <div className="rounded-md rounded-t-none overflow-x-auto relative">
          <table className="w-full table-auto" style={optimizedStyles.table}>
            <thead className="bg-gray-200 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left text-xs text-gray-600 max-w-sm">
                  Code
                </th>
                <th className="px-3 py-2 text-left text-xs text-gray-600">
                  Description
                </th>
                <th className="px-3 py-2 text-left text-xs text-gray-600">
                  Unit
                </th>
                <th className="px-3 py-2 text-left text-xs text-gray-600">
                  Category
                </th>
                <th className="px-3 py-2 text-left text-xs text-gray-600">
                  Total Stock
                </th>
                <th className="px-3 py-2 text-left text-xs text-gray-600">
                  Brand Stock
                </th>
                <th className="px-3 py-2 text-left text-xs text-gray-600">
                  Group
                </th>
                <th className="px-3 py-2 text-left text-xs text-gray-600">
                  Sub Group
                </th>
                <th className="px-3 py-2 text-left text-xs text-gray-600">
                  Price
                </th>
              </tr>
            </thead>
            <tbody>
              {suggestions.map((suggestion, index) => (
                <SuggestionRow
                  key={`${suggestion.Code}-${index}`}
                  ref={(el) => (rowRefs.current[index] = el)}
                  suggestion={suggestion}
                  isSelected={selectedSuggestionIndex === index}
                  onSelect={(suggestion) => {
                    selectCodeSuggestion(activeSuggestionRowId, suggestion);
                    console.log("Suggestion data", suggestion);
                  }}
                  onHover={() => setSelectedSuggestionIndex(index)}
                />
              ))}
            </tbody>
          </table>

          {/* Load more trigger */}
          {hasMoreSuggestions && (
            <div ref={loadMoreTriggerRef} className="py-2">
              {isFetchingMore && (
                <div className="flex items-center justify-center gap-2">
                  <Loader className="animate-spin h-4 w-4 text-gray-500" />
                  <span className="text-xs text-gray-500">Loading more...</span>
                </div>
              )}
            </div>
          )}

          {!hasMoreSuggestions && suggestions.length > 0 && (
            <div className="px-3 py-2 text-xs text-gray-500 bg-gray-50 border-t text-center">
              All results loaded ({suggestions.length} items)
            </div>
          )}
        </div>
      ) : (
        <div className="px-3 py-2 text-gray-500">No suggestions found</div>
      )}
    </div>
  );
};

export default memo(InvoiceSuggestionList);
